import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { ArrowLeft, Maximize2, Minimize2, Trash2 } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Card } from '@cortex/web-design-system/components/ui/Card';
import { Modal } from '@cortex/web-design-system/components/ui/Modal';
import { reportApi } from '../lib/report-api.js';
import { queryKeys } from '../lib/query-keys.js';

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business ISO 日時文字列を YYYY/MM/DD HH:MM 形式に整形する。
 * @graph-connects none
 */
function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${String(d.getFullYear())}/${pad(d.getMonth() + 1)}/${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business HTML本文を sandbox iframe で安全にレンダリングするコンポーネント。CSP 違反やスクリプト実行を防ぐ。
 * @graph-connects none
 */
function HtmlRenderer({ html, fullscreen }: { html: string; fullscreen: boolean }) {
  const { t } = useTranslation();
  /** @graph-connects none */
  const srcdoc = useMemo(
    () =>
      [
        '<!DOCTYPE html><html><head><meta charset="utf-8"><style>',
        'body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif; line-height: 1.6; margin: 0; padding: 1rem; color: inherit; background: transparent; }',
        '@media (prefers-color-scheme: dark) { body { color: #e8e4df; } }',
        'table { border-collapse: collapse; width: 100%; font-size: 0.85rem; }',
        'th, td { padding: 0.4rem 0.6rem; border-bottom: 1px solid rgba(128,128,128,0.2); text-align: left; }',
        'img { max-width: 100%; height: auto; }',
        '</style></head><body>',
        html,
        '</body></html>',
      ].join('\n'),
    [html],
  );

  return (
    <iframe
      title={t('reportDetail.bodyTitle')}
      srcDoc={srcdoc}
      sandbox="allow-same-origin"
      className="w-full border-0"
      style={{
        minHeight: fullscreen ? undefined : '600px',
        height: fullscreen ? '100%' : undefined,
      }}
    />
  );
}

/**
 * @graph-node {Page}
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business レポートストアの詳細ページ。スポット分析レポートのHTML本文をレンダリング表示する。投稿者のみ削除可能。全画面表示モードでサイドバーを隠してレポートを大きく表示できる。
 * @graph-connects reportApi [calls] レポート詳細・削除 API
 * @graph-connects queryKeys [calls] react-query キーファクトリ
 */
export function ReportDetailPage() {
  const { t } = useTranslation();
  const params = useParams({ strict: false });
  const id = (params as { id?: string }).id ?? '';
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isFullscreen, setIsFullscreen] = useState(false);

  /** @graph-connects none */
  const closeFullscreen = useCallback(() => {
    setIsFullscreen(false);
  }, []);

  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.reports.detail(id),
    queryFn: () => reportApi.get(id),
    enabled: id.length > 0,
  });

  const deleteMutation = useMutation({
    mutationFn: () => reportApi.delete(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.reports.all });
      void navigate({ to: '/reports' });
    },
  });

  if (isLoading) {
    return (
      <div className="py-16 text-center text-sm text-[var(--color-text-muted)]">
        {t('app.loading')}
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-10">
        <Card className="border-[var(--color-error)]/20 p-6 text-center">
          <p className="text-sm text-[var(--color-error)]">{t('reportDetail.fetchError')}</p>
        </Card>
      </div>
    );
  }

  if (!data) return null;

  const { report, canEdit } = data;

  return (
    <>
      <div className="mx-auto w-full max-w-4xl px-4 py-10">
        <div className="mb-6">
          <Link
            to="/reports"
            className="inline-flex items-center gap-1 text-sm text-[var(--color-text-secondary)] hover:text-[var(--color-accent-primary)]"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            {t('common.backToList')}
          </Link>
        </div>

        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <div className="mb-1 flex items-center gap-2">
              <span className="inline-flex items-center rounded-full bg-[var(--color-accent-primary)]/10 px-2 py-0.5 text-xs font-medium text-[var(--color-accent-primary)]">
                {report.category}
              </span>
              <span className="text-xs text-[var(--color-text-muted)]">
                {formatDateTime(report.createdAt)}
              </span>
            </div>
            <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">{report.title}</h1>
            {report.summary && (
              <p className="mt-1 text-sm text-[var(--color-text-secondary)]">{report.summary}</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              onClick={() => {
                setIsFullscreen(true);
              }}
            >
              <Maximize2 className="mr-1 h-4 w-4" aria-hidden="true" />
              {t('reportDetail.fullscreen')}
            </Button>
            {canEdit && (
              <Button
                variant="secondary"
                onClick={() => {
                  if (window.confirm(t('reportDetail.deleteConfirm'))) {
                    deleteMutation.mutate();
                  }
                }}
                disabled={deleteMutation.isPending}
              >
                <Trash2 className="mr-1 h-4 w-4" aria-hidden="true" />
                {t('common.delete')}
              </Button>
            )}
          </div>
        </div>

        {!isFullscreen && (
          <Card className="overflow-hidden p-0">
            <HtmlRenderer html={report.htmlContent} fullscreen={false} />
          </Card>
        )}
      </div>

      <Modal
        open={isFullscreen}
        onClose={closeFullscreen}
        size="fullscreen"
        title={
          <span className="flex items-center gap-3">
            <span className="inline-flex shrink-0 items-center rounded-full bg-[var(--color-accent-primary)]/10 px-2 py-0.5 text-xs font-medium text-[var(--color-accent-primary)]">
              {report.category}
            </span>
            <span className="truncate">{report.title}</span>
          </span>
        }
        footer={
          <div className="flex justify-end">
            <Button variant="secondary" onClick={closeFullscreen}>
              <Minimize2 className="mr-1 h-4 w-4" aria-hidden="true" />
              {t('common.close')}
            </Button>
          </div>
        }
      >
        <div className="h-full -mx-6">
          <HtmlRenderer html={report.htmlContent} fullscreen={true} />
        </div>
      </Modal>
    </>
  );
}
